import classnames from "classnames";
import React from "react";
import MultiplyOnHoverButton from "web/react/components/buttons/multiply-on-hover-button/multiply-on-hover-button";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import TrackedButton from "web/react/components/tracked-button/tracked-button";
import analytics from "web/script/analytics/analytics";
import { CartSummaryItem, CartSummaryPromotionSerializer } from "web/types/serializers";
// eslint-disable-next-line css-modules/no-unused-class
import styles from "./cart-summary.css";

interface CartSummaryLastAddedProps {
    checkoutUrl?: string | null;
    item?: CartSummaryItem | null;
    itemCount?: number | null;
    totalPrice?: string | null;
    promotions?: CartSummaryPromotionSerializer[];
    productAnalyticsData: any;
    displayTotalPrice: boolean;
}

function CartSummaryPromoSection({
    promotions,
}: {
    promotions?: CartSummaryPromotionSerializer[];
}): JSX.Element | null {
    if (!promotions || !promotions?.length) {
        return null;
    }

    return (
        <div className={styles.promoSection}>
            {promotions.map((promotion, index) => {
                if (promotion && promotion.promotion_text) {
                    return (
                        <div
                            key={promotion.promotion_text}
                            className={classnames(styles.promotion, { mt10: index > 0 })}
                        >
                            <SVGIcon
                                className={classnames({
                                    [styles.tagIcon]: !promotion.is_shipping_related,
                                })}
                                name={promotion.is_shipping_related ? "truck" : "tag"}
                            />

                            <span>{promotion.promotion_text}</span>
                        </div>
                    );
                } else {
                    return null;
                }
            })}
        </div>
    );
}

function CartSummaryTotals({
    itemCount,
    totalPrice,
}: {
    itemCount: number;
    totalPrice?: string;
}): JSX.Element {
    return (
        <div>
            <h5 className={classnames(styles.title, "mb5")}>{"Your current bag"}</h5>

            <div className={styles.summary}>
                <div className={styles.summaryRow}>
                    <div>{"Number of items:"}</div>
                    <div className={styles.summaryValue}>{itemCount}</div>
                </div>

                <div className={styles.summaryRow}>
                    <div>{"Total Cost:"}</div>
                    <div className={styles.summaryValue}>
                        <em>{totalPrice}</em>
                    </div>
                </div>
            </div>
        </div>
    );
}

function CartSummaryCheckoutButton({
    url,
    productAnalyticsData,
}: {
    url: string;
    productAnalyticsData: any;
}): JSX.Element {
    async function handleClick(): Promise<void> {
        productAnalyticsData?.forEach((product) => {
            analytics.ecommerce("ec:addProduct", {
                name: product.product_canonical_slug,
                category: product.category,
                brand: product.designer_slug,
                variant: product.product_uid,
                price: product.price,
                quantity: 1,
                dimension20: product.gender,
                metric8: product.sale_discount,
            });
        });

        analytics.ecommerce("ec:setAction", "checkout", {
            step: 1,
        });

        await analytics.event("CART_SUMMARY", "CHECKOUT");
        (window.location as any) = url;
    }

    return (
        <MultiplyOnHoverButton
            disabled={false}
            fullWidth={true}
            fullHeight={false}
            openInNewTab={false}
            className={styles.cta}
            onClick={handleClick}
            data-test-id="checkout-button"
        >
            {"Check out now"}
        </MultiplyOnHoverButton>
    );
}

export default function CartSummaryLastAdded({
    checkoutUrl,
    item,
    promotions,
    itemCount,
    totalPrice,
    productAnalyticsData,
    displayTotalPrice,
}: CartSummaryLastAddedProps): JSX.Element {
    return (
        <>
            <div>
                <h5 className={styles.title}>{"Last added to your bag..."}</h5>

                <TrackedButton
                    eventCategory="cart-summary"
                    eventAction="last-added-item"
                    eventLabel=""
                    openInNewTab={false}
                    href={item?.url || ""}
                    className={styles.item}
                >
                    <img className={styles.image} src={item?.image || ""} />

                    <div className={styles.details}>
                        <div className={styles.detail}>{item?.designer}</div>

                        <div
                            className={classnames(styles.detail, {
                                [styles.onSale]: item?.on_sale,
                            })}
                        >
                            <span className={styles.normalPrice}>
                                {item?.normal_price_with_currency_symbol}
                            </span>

                            {item?.on_sale ? (
                                <span className={styles.salePrice}>
                                    {" "}
                                    {item?.sale_price_with_currency_symbol}
                                </span>
                            ) : null}
                        </div>

                        <div className={styles.detail} data-test-id="size-display">
                            {item?.size !== item?.raw_size
                                ? `Size: ${item?.size} / ${item?.raw_size}`
                                : `Size: ${item?.size}`}
                        </div>
                    </div>

                    <SVGIcon name="chevron" className={styles.chevronIcon} />
                </TrackedButton>
            </div>
            <CartSummaryPromoSection promotions={promotions} />
            {displayTotalPrice && (
                <CartSummaryTotals itemCount={itemCount || 0} totalPrice={totalPrice || ""} />
            )}
            <CartSummaryCheckoutButton
                url={checkoutUrl || ""}
                productAnalyticsData={productAnalyticsData}
            />
        </>
    );
}
